import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
  }
}

// Custom checkbox styles
const checkboxStyles = `
  .custom-checkbox {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }
  
  .custom-checkbox input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  .checkbox-box {
    width: 22px;
    height: 22px;
    border: 2px solid #ddd;
    border-radius: 5px;
    background-color: white;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .custom-checkbox:hover .checkbox-box {
    border-color: #03A84A;
  }
  
  .custom-checkbox input[type="checkbox"]:checked ~ .checkbox-box {
    background-color: #03A84A;
    border-color: #03A84A;
  }
  
  .checkbox-box::after {
    content: "✓";
    color: white;
    font-size: 16px;
    font-weight: bold;
    opacity: 0;
    transform: scale(0);
    transition: all 0.2s ease;
  }
  
  .custom-checkbox input[type="checkbox"]:checked ~ .checkbox-box::after {
    opacity: 1;
    transform: scale(1);
  }
  
  .custom-radio {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }
  
  .custom-radio input[type="radio"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }
  
  .radio-box {
    width: 20px;
    height: 20px;
    border: 2px solid #ddd;
    border-radius: 50%;
    background-color: white;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .custom-radio:hover .radio-box {
    border-color: #03A84A;
  }
  
  .custom-radio input[type="radio"]:checked ~ .radio-box {
    border-color: #03A84A;
  }
  
  .radio-box::after {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #03A84A;
    opacity: 0;
    transform: scale(0);
    transition: all 0.2s ease;
  }
  
  .custom-radio input[type="radio"]:checked ~ .radio-box::after {
    opacity: 1;
    transform: scale(1);
  }
`;

const Volunteer: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    contactNumber: '',
    gender: '',
    email: '',
    address: '',
    availableDays: [] as string[],
    preferredTime: [] as string[],
    areasOfInterest: [] as string[],
    otherArea: '',
    hasExperience: '',
    experienceDetails: '',
    emergencyName: '',
    emergencyRelationship: '',
    emergencyContact: '',
    educationLevel: '',
    skills: '',
    languages: '',
    agreedToRules: false
  });

  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    // Add custom checkbox styles
    const styleSheet = document.createElement("style");
    styleSheet.textContent = checkboxStyles;
    document.head.appendChild(styleSheet);

    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }
  }, []);

  const handleCheckboxChange = (field: 'availableDays' | 'preferredTime' | 'areasOfInterest', value: string) => {
    setFormData(prev => {
      const currentArray = prev[field];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitMessage('');
    setSubmitError('');

    if (!formData.agreedToRules) {
      setSubmitError('Please confirm that you agree to follow all rules and guidelines');
      return;
    }

    try {
      const response = await fetch('http://localhost:5000/api/volunteers/applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage('Application submitted successfully! We will contact you soon.');
        // Reset form
        setFormData({
          fullName: '',
          dateOfBirth: '',
          contactNumber: '',
          gender: '',
          email: '',
          address: '',
          availableDays: [],
          preferredTime: [],
          areasOfInterest: [],
          otherArea: '',
          hasExperience: '',
          experienceDetails: '',
          emergencyName: '',
          emergencyRelationship: '',
          emergencyContact: '',
          educationLevel: '',
          skills: '',
          languages: '',
          agreedToRules: false
        });
        window.scrollTo(0, 0);
      } else {
        setSubmitError(data.error || 'Failed to submit application');
      }
    } catch (error) {
      setSubmitError('Failed to submit application. Please try again.');
    }
  };

  return (
    <>
      {/* Page banner */}
      <section className="banner__inner-page bg-image pt-160 pb-160"
        data-background="/assets/images/banner/banner-inner-page.jpg">
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Volunteer With Us</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Volunteer</span>
          </div>
        </div>
      </section>

      {/* Volunteer Application Section */}
      <section className="pt-120 pb-120">
        <div className="container">
          <div className="row justify-content-center mb-60">
            <div className="col-lg-10">
              <div className="text-center mb-40">
                <h2 style={{ color: '#0C2239', fontSize: '36px', marginBottom: '15px' }}>
                  Volunteer Application Form
                </h2>
                <p style={{ color: '#6F7174', fontSize: '16px', lineHeight: '1.8' }}>
                  Thank you for your interest in volunteering with New Life Nepal. Please fill out this form completely to help us understand how you can best contribute to our mission.
                </p>
              </div>

              {submitMessage && (
                <div style={{ 
                  padding: '15px 20px', 
                  backgroundColor: '#d4edda', 
                  border: '1px solid #c3e6cb', 
                  borderRadius: '8px',
                  color: '#155724',
                  marginBottom: '30px',
                  textAlign: 'center'
                }}>
                  {submitMessage}
                </div>
              )}

              {submitError && (
                <div style={{ 
                  padding: '15px 20px', 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  borderRadius: '8px',
                  color: '#721c24',
                  marginBottom: '30px',
                  textAlign: 'center'
                }}>
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Personal Information */}
                <div className="wow fadeInUp" data-wow-delay="100ms" data-wow-duration="1500ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '30px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-user me-2" style={{ color: '#03A84A' }}></i>
                    Personal Information
                  </h4>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Full Name <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          border: '2px solid #e8e8e8',
                          borderRadius: '8px',
                          fontSize: '15px',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#e8e8e8'}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Date of Birth <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.dateOfBirth}
                        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '15px'
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Contact Number <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.contactNumber}
                        onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '15px'
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Gender <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <div style={{ display: 'flex', gap: '25px', paddingTop: '12px' }}>
                        <label className="custom-radio">
                          <input
                            type="radio"
                            name="gender"
                            value="Male"
                            checked={formData.gender === 'Male'}
                            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            required
                          />
                          <span className="radio-box"></span>
                          <span style={{ marginLeft: '10px', color: '#0C2239', fontWeight: '500' }}>Male</span>
                        </label>
                        <label className="custom-radio">
                          <input
                            type="radio"
                            name="gender"
                            value="Female"
                            checked={formData.gender === 'Female'}
                            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            required
                          />
                          <span className="radio-box"></span>
                          <span style={{ marginLeft: '10px', color: '#0C2239', fontWeight: '500' }}>Female</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Email <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '15px'
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Address <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '15px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Availability */}
                <div className="wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '30px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-calendar-days me-2" style={{ color: '#03A84A' }}></i>
                    Availability
                  </h4>

                  <div className="mb-4">
                    <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
                      Available Days
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                        <label key={day} className="custom-checkbox" style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px', 
                          cursor: 'pointer', 
                          minWidth: '140px',
                          padding: '12px 18px',
                          backgroundColor: formData.availableDays.includes(day) ? '#FFF5F2' : '#f9f9f9',
                          border: `2px solid ${formData.availableDays.includes(day) ? '#03A84A' : '#e8e8e8'}`,
                          borderRadius: '8px',
                          transition: 'all 0.3s ease'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.availableDays.includes(day)}
                            onChange={() => handleCheckboxChange('availableDays', day)}
                            style={{ display: 'none' }}
                          />
                          <span className="checkbox-box"></span>
                          <span style={{ fontWeight: formData.availableDays.includes(day) ? '600' : '500', color: '#0C2239' }}>{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
                      Preferred Time
                    </label>
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      {['Morning', 'Afternoon', 'Evening'].map(time => (
                        <label key={time} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px', 
                          cursor: 'pointer',
                          padding: '12px 25px',
                          backgroundColor: formData.preferredTime.includes(time) ? '#FFF5F2' : '#f9f9f9',
                          border: `2px solid ${formData.preferredTime.includes(time) ? '#03A84A' : '#e8e8e8'}`,
                          borderRadius: '8px',
                          transition: 'all 0.3s ease',
                          minWidth: '140px'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.preferredTime.includes(time)}
                            onChange={() => handleCheckboxChange('preferredTime', time)}
                            style={{ 
                              width: '20px', 
                              height: '20px',
                              cursor: 'pointer',
                              accentColor: '#03A84A'
                            }}
                          />
                          <span style={{ fontWeight: formData.preferredTime.includes(time) ? '600' : '500', color: '#0C2239' }}>{time}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Area of Interest */}
                <div className="wow fadeInUp" data-wow-delay="300ms" data-wow-duration="1500ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '30px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-heart me-2" style={{ color: '#03A84A' }}></i>
                    Area of Interest
                  </h4>

                  <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
                    Which area(s) would you want to volunteer in?
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                    {['Child Care', 'Teaching', 'Therapy Support', 'Stroke', 'Administrative Support'].map(area => (
                      <label key={area} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        cursor: 'pointer', 
                        minWidth: '180px',
                        padding: '12px 20px',
                        backgroundColor: formData.areasOfInterest.includes(area) ? '#FFF5F2' : '#f9f9f9',
                        border: `2px solid ${formData.areasOfInterest.includes(area) ? '#03A84A' : '#e8e8e8'}`,
                        borderRadius: '8px',
                        transition: 'all 0.3s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.areasOfInterest.includes(area)}
                          onChange={() => handleCheckboxChange('areasOfInterest', area)}
                          style={{ 
                            width: '20px', 
                            height: '20px',
                            cursor: 'pointer',
                            accentColor: '#03A84A'
                          }}
                        />
                        <span style={{ fontWeight: formData.areasOfInterest.includes(area) ? '600' : '500', color: '#0C2239' }}>{area}</span>
                      </label>
                    ))}
                  </div>
                  <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Other:
                  </label>
                  <input
                    type="text"
                    value={formData.otherArea}
                    onChange={(e) => setFormData({ ...formData, otherArea: e.target.value })}
                    placeholder="Please specify"
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '15px'
                    }}
                  />
                </div>

                {/* Skills and Experience */}
                <div className="wow fadeInUp" data-wow-delay="400ms" data-wow-duration="1500ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '30px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-briefcase me-2" style={{ color: '#03A84A' }}></i>
                    Skills and Experience
                  </h4>

                  <div className="mb-3">
                    <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
                      Do you have any past volunteering experience?
                    </label>
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="hasExperience"
                          value="Yes"
                          checked={formData.hasExperience === 'Yes'}
                          onChange={(e) => setFormData({ ...formData, hasExperience: e.target.value })}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="hasExperience"
                          value="No"
                          checked={formData.hasExperience === 'No'}
                          onChange={(e) => setFormData({ ...formData, hasExperience: e.target.value })}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  {formData.hasExperience === 'Yes' && (
                    <div>
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        If yes, specify:
                      </label>
                      <textarea
                        value={formData.experienceDetails}
                        onChange={(e) => setFormData({ ...formData, experienceDetails: e.target.value })}
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '15px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Emergency Contact & Background Info */}
                <div className="wow fadeInUp" data-wow-delay="500ms" data-wow-duration="1500ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <h4 style={{ 
                        color: '#0C2239', 
                        paddingBottom: '15px',
                        marginBottom: '25px',
                        fontSize: '20px',
                        fontWeight: '700',
                        borderBottom: '3px solid #03A84A',
                        display: 'inline-block'
                      }}>
                        <i className="fa-solid fa-phone-volume me-2" style={{ color: '#03A84A' }}></i>
                        Emergency Contact
                      </h4>
                      <div className="mb-3">
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Name <span style={{ color: '#03A84A' }}>*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.emergencyName}
                          onChange={(e) => setFormData({ ...formData, emergencyName: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '15px'
                          }}
                        />
                      </div>
                      <div className="mb-3">
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Relationship <span style={{ color: '#03A84A' }}>*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.emergencyRelationship}
                          onChange={(e) => setFormData({ ...formData, emergencyRelationship: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '15px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Contact Number <span style={{ color: '#03A84A' }}>*</span>
                        </label>
                        <input
                          type="tel"
                          required
                          value={formData.emergencyContact}
                          onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '15px'
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <h4 style={{ 
                        color: '#0C2239', 
                        paddingBottom: '15px',
                        marginBottom: '25px',
                        fontSize: '20px',
                        fontWeight: '700',
                        borderBottom: '3px solid #03A84A',
                        display: 'inline-block'
                      }}>                        <i className="fa-solid fa-id-card me-2" style={{ color: '#03A84A' }}></i>                        Volunteer Background Info
                      </h4>
                      <div className="mb-3">
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Education Level
                        </label>
                        <input
                          type="text"
                          value={formData.educationLevel}
                          onChange={(e) => setFormData({ ...formData, educationLevel: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '15px'
                          }}
                        />
                      </div>
                      <div className="mb-3">
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Skills
                        </label>
                        <input
                          type="text"
                          value={formData.skills}
                          onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                          placeholder="e.g., Teaching, First Aid, Art"
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '15px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Languages
                        </label>
                        <input
                          type="text"
                          value={formData.languages}
                          onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                          placeholder="e.g., Nepali, English, Hindi"
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '15px'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Volunteer Guidelines & Rules */}
                <div className="wow fadeInUp" data-wow-delay="600ms" data-wow-duration="1500ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-shield-heart me-2" style={{ color: '#03A84A' }}></i>
                    Volunteer Guidelines & Rules
                  </h4>

                  <p style={{ color: '#0C2239', marginBottom: '20px', lineHeight: '1.8' }}>
                    To keep our children safe, comfortable, and supported, all volunteers are expected to follow the guidelines below. These help maintain a stable routine for our kids, because many of them have neurodevelopmental disorders and rely heavily on structure.
                  </p>

                  <div style={{ 
                    backgroundColor: '#FFF5F2', 
                    padding: '25px',
                    borderRadius: '8px',
                    border: '2px solid #03A84A'
                  }}>
                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      display: 'grid',
                      gap: '12px'
                    }}>
                      {[
                        "Always follow the care team's instructions when interacting with them.",
                        "Do not force physical contact, conversations, or activities.",
                        "Volunteers must not interrupt or change daily routines.",
                        "Any planned involvement must be approved by staff beforehand.",
                        "Social media posting without permission is strictly prohibited.",
                        "Avoid loud voices, sudden movements, or anything that may distress the children.",
                        "Arrive on time for the days and hours you commit to.",
                        "Inform us in advance if you cannot attend.",
                        "Uninformed absence may lead to discontinuation of volunteering privileges.",
                        "Wear comfortable, modest, and respectful clothing.",
                        "Avoid strong perfumes or accessories that may distract or frighten children.",
                        "Any harassment, misuse of resources, or behavior that risks a child's well-being will result in immediate removal from the volunteer program.",
                        "Our children have neurodevelopmental conditions, and sudden changes or unfamiliar activities can affect them.",
                        "Personal details, photos, and videos of children or staff cannot be shared outside the organization.",
                        "Do not give children snacks, money, personal items, or bring outside food without approval.",
                        "Volunteers are not allowed to lift, carry, or physically handle a child without staff supervision."
                      ].map((rule, index) => (
                        <li key={index} style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '10px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          fontSize: '14px',
                          lineHeight: '1.6'
                        }}>
                          <i className="fa-solid fa-check-circle" style={{ color: '#03A84A', marginTop: '2px', flexShrink: 0 }}></i>
                          <span style={{ color: '#0C2239' }}>{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{ marginTop: '25px' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '15px',
                      backgroundColor: '#E8F4FD',
                      borderRadius: '8px',
                      border: '2px solid #3498db'
                    }}>
                      <input
                        type="checkbox"
                        required
                        checked={formData.agreedToRules}
                        onChange={(e) => setFormData({ ...formData, agreedToRules: e.target.checked })}
                        style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer' }}
                      />
                      <span style={{ color: '#0C2239', fontWeight: '600', fontSize: '15px' }}>
                        By signing below, I confirm that the information submitted is correct and understand that it will be used for official records at New Life Nepal. I agree to follow all the guidelines and rules mentioned above.
                      </span>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    style={{
                      padding: '15px 50px',
                      backgroundColor: '#03A84A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 5px 15px rgba(247, 79, 34, 0.3)'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#01944B'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#03A84A'}
                  >
                    Submit Application
                  </button>
                </div>
              </form>

              {/* Contact Information */}
              <div style={{
                marginTop: '50px',
                padding: '30px',
                backgroundColor: '#E8F4FD',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <h5 style={{ color: '#0C2239', marginBottom: '15px' }}>Contact Us</h5>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa-solid fa-phone" style={{ color: '#03A84A' }}></i>
                    <span>+977 9851031573</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa-solid fa-location-dot" style={{ color: '#03A84A' }}></i>
                    <span>Gokarneshwor-5, Kathmandu</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa-solid fa-globe" style={{ color: '#03A84A' }}></i>
                    <span>www.newlifenepal.org.np</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Volunteer;
